import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE from "../utils/api";

const money = (v) => `Rs. ${Number(v || 0).toLocaleString("en-IN")}`;

const PRESETS = [
  { key: "this_month", label: "This Month" },
  { key: "last_3_months", label: "Last 3 Months" },
  { key: "this_year", label: "This Year" },
  { key: "all_time", label: "All Time" },
  { key: "custom", label: "Custom Range" },
];

const presetToRange = (preset) => {
  const now = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === "this_month") {
    return { startDate: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), endDate: fmt(now) };
  }
  if (preset === "last_3_months") {
    return { startDate: fmt(new Date(now.getFullYear(), now.getMonth() - 2, 1)), endDate: fmt(now) };
  }
  if (preset === "this_year") {
    return { startDate: fmt(new Date(now.getFullYear(), 0, 1)), endDate: fmt(now) };
  }
  return { startDate: "", endDate: "" }; // all_time / custom (custom filled in by the date inputs)
};

// Expenses (spent) vs fee amounts actually received, with date-range
// filters — shared between the admin and principal dashboards.
const FinanceOverviewWidget = () => {
  const [preset, setPreset] = useState("this_year");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const range = preset === "custom" ? { startDate: customStart, endDate: customEnd } : presetToRange(preset);
      const params = new URLSearchParams();
      if (range.startDate) params.append("startDate", range.startDate);
      if (range.endDate) params.append("endDate", range.endDate);
      const res = await axios.get(`${API_BASE}/reports/finance-overview?${params.toString()}`);
      setReport(res.data);
    } catch (_) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [preset, customStart, customEnd]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const data = report?.data || [];
  const maxValue = Math.max(1, ...data.map((d) => Math.max(d.expenses, d.received)));

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-lg font-semibold text-gray-800">💸 Expenses vs Amount Received</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={preset} onChange={(e) => setPreset(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm">
            {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          {preset === "custom" && (
            <>
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" />
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm" />
            </>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
      ) : !report ? (
        <p className="text-sm text-gray-400 text-center py-8">No data available.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-xs text-red-600 font-medium uppercase">Expenses</p>
              <p className="text-2xl font-bold text-red-700 mt-1">{money(report.summary.totalExpenses)}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-xs text-green-600 font-medium uppercase">Received</p>
              <p className="text-2xl font-bold text-green-700 mt-1">{money(report.summary.totalReceived)}</p>
            </div>
            <div className={`rounded-lg p-4 ${report.summary.net >= 0 ? "bg-indigo-50" : "bg-orange-50"}`}>
              <p className={`text-xs font-medium uppercase ${report.summary.net >= 0 ? "text-indigo-600" : "text-orange-600"}`}>Net</p>
              <p className={`text-2xl font-bold mt-1 ${report.summary.net >= 0 ? "text-indigo-700" : "text-orange-700"}`}>{money(report.summary.net)}</p>
            </div>
          </div>

          {data.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No expenses or payments recorded in this range.</p>
          ) : (
            <>
              <div className="flex items-end gap-3 h-40 border-b border-gray-200 pb-1 overflow-x-auto">
                {data.map((d) => (
                  <div key={d.month} className="flex flex-col items-center gap-1 flex-1 min-w-[2.5rem] h-full justify-end">
                    <div className="flex items-end gap-0.5 h-full">
                      <div
                        className="w-3 bg-red-400 rounded-t"
                        style={{ height: `${Math.max((d.expenses / maxValue) * 100, d.expenses > 0 ? 2 : 0)}%` }}
                        title={`Expenses: ${money(d.expenses)}`}
                      />
                      <div
                        className="w-3 bg-green-400 rounded-t"
                        style={{ height: `${Math.max((d.received / maxValue) * 100, d.received > 0 ? 2 : 0)}%` }}
                        title={`Received: ${money(d.received)}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 overflow-x-auto">
                {data.map((d) => (
                  <span key={d.month} className="flex-1 min-w-[2.5rem] text-center text-xs text-gray-500 pt-1">{d.month}</span>
                ))}
              </div>
              <div className="flex gap-4 mt-3 text-xs text-gray-600">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded inline-block" /> Expenses</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded inline-block" /> Received</span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default FinanceOverviewWidget;
